import { css } from "@emotion/react";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useSnackbar } from "notistack";
import { Suspense, useState } from "react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/Badge";
import { Button } from "@/components/Button";
import { Empty } from "@/components/Empty";
import { InfoBox } from "@/components/InfoBox";
import { BarLoading } from "@/components/Loading/BarLoading";
import { Typography } from "@/components/Typography";
import { interlude, type InterludeTypes } from "@/interlude";
import { useMetaAdsLogin } from "@/pages/applications/hooks/useMetaAdsLogin";
import { DisconnectMetaAdsModal } from "@/pages/applications/pages/metaAdsIntegration/DisconnectMetaAdsModal";
import { PageLayout } from "@/pages/components/PageLayout";
import { PageTitle } from "@/pages/components/PageTitle";
import MetaAdsImage from "@/statics/metaAds.svg";

const cssLink = css`
  text-decoration: none;
`;

const cssPageTitle = css`
  margin-bottom: 0;
`;

const styles = {
  section: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    maxWidth: "512px",
  },
  accountCard: {
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
    padding: "16px",
    backgroundColor: theme.colors.staticBgContainer,
    borderRadius: "8px",
  },
  accountIcon: {
    width: 24,
    height: 24,
    flexShrink: 0,
  },
  accountInfo: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    flex: 1,
  },
  buttonGroup: {
    display: "flex",
    gap: "8px",
    mt: "8px",
  },
  emptyRow: {
    border: "none",
  },
  emptyContainer: {
    padding: "24px 12px",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

interface MetaAdsIntegrationPageInternalProps {
  showReconnectBanner?: boolean;
}

const MetaAdsIntegrationPageInternal: React.FC<MetaAdsIntegrationPageInternalProps> =
  function MetaAdsIntegrationPageInternal({ showReconnectBanner = false }) {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();
    const [isDisconnectModalOpen, setIsDisconnectModalOpen] = useState(false);

    const statusQuery = interlude.metaAds.useGetStatus();
    const accountsQuery = interlude.metaAds.useListAccounts(undefined, {
      enabled:
        statusQuery.isSuccess && statusQuery.data?.tokenStatus === "active",
    });

    const disconnectMutation = interlude.metaAds.useDisconnect(undefined, {
      onSuccess: () => {
        setIsDisconnectModalOpen(false);
        enqueueSnackbar(t("applications.metaAds.notifications.disconnected"), {
          variant: "success",
        });
      },
      onError: () => {
        setIsDisconnectModalOpen(false);
      },
    });

    const hydrateConnectResponse = (
      data: InterludeTypes["MetaAdsConnectResponse"],
    ) => {
      queryClient.setQueryData(
        interlude.metaAds.getKeyByAlias("getStatus"),
        data.token,
      );
      queryClient.setQueryData(
        interlude.metaAds.getKeyByAlias("listAccounts"),
        {
          accounts: data.accounts,
          total: data.accounts.length,
        },
      );
      queryClient.setQueryData(
        interlude.metaAds.getKeyByAlias("listDatasets"),
        data.datasets,
      );
    };

    const connectMutation = interlude.metaAds.useConnect(undefined, {
      onSuccess: (data) => {
        hydrateConnectResponse(data);
        enqueueSnackbar(t("applications.metaAds.notifications.connected"), {
          variant: "success",
        });
      },
    });

    const metaAdsLogin = useMetaAdsLogin({
      onSuccess: ({ authResponse }) => {
        connectMutation.mutate({ code: authResponse.code });
      },
    });

    const handleConnect = () => {
      metaAdsLogin.startLogin();
    };

    const isStatusLoading = statusQuery.isPending || statusQuery.isRefetching;
    const isConnected =
      statusQuery.isSuccess && statusQuery.data?.tokenStatus === "active";
    const isExpired =
      statusQuery.isSuccess && statusQuery.data?.tokenStatus === "expired";
    const hasToken = isConnected || isExpired;
    const connectedName = statusQuery.data?.name ?? null;
    const accounts = accountsQuery.data?.accounts ?? [];
    const isAccountsLoading =
      accountsQuery.isPending || accountsQuery.isRefetching;

    const handleConfirmDisconnect = () => {
      disconnectMutation.mutate(undefined);
    };

    return (
      <Stack gap="8px">
        <Breadcrumbs aria-label="breadcrumb" separator=">">
          <Link to="/applications" css={cssLink}>
            <Typography variant="body" color={theme.colors.staticFgBody}>
              {t("applications.title")}
            </Typography>
          </Link>
          <Typography variant="body" color={theme.colors.staticFgNote}>
            {t("applications.metaAds.title")}
          </Typography>
        </Breadcrumbs>
        <PageTitle css={cssPageTitle}>
          {t("applications.metaAds.title")}
        </PageTitle>

        {statusQuery.isPending ? (
          <BarLoading />
        ) : (
          <Stack gap="24px" sx={{ pt: "24px" }}>
            {showReconnectBanner ? (
              <InfoBox
                variant="warning"
                description={t(
                  "applications.metaAds.reconnectBanner.description",
                )}
                action={{
                  label: t("applications.metaAds.reconnectBanner.action"),
                  onClick: handleConnect,
                }}
              />
            ) : null}

            {/* Meta Ads connection section */}
            <Stack gap="8px" sx={styles.section}>
              <Typography
                variant="h3"
                fontWeight="medium"
                color={theme.colors.staticFgTitle}
              >
                {t("applications.metaAds.connection.title")}
              </Typography>
              <Typography variant="note" color={theme.colors.staticFgNote}>
                {t("applications.metaAds.connection.description")}
              </Typography>
              {isStatusLoading ? (
                <Box sx={styles.accountCard}>
                  <Skeleton variant="circular" width={24} height={24} />
                  <Box sx={styles.accountInfo}>
                    <Skeleton variant="text" width={150} height={20} />
                    <Skeleton variant="text" width={100} height={16} />
                  </Box>
                  <Skeleton variant="rounded" width={80} height={24} />
                </Box>
              ) : hasToken ? (
                <Box sx={styles.accountCard}>
                  <Box
                    component="img"
                    src={MetaAdsImage}
                    alt="Meta Ads"
                    sx={styles.accountIcon}
                  />
                  <Box sx={styles.accountInfo}>
                    <Typography
                      variant="body"
                      fontWeight={400}
                      color={theme.colors.staticFgTitle}
                    >
                      {connectedName ??
                        t(
                          "applications.metaAds.connection.fallbackAccountName",
                        )}
                    </Typography>
                  </Box>
                  {isConnected ? (
                    <Badge variant="positive">
                      {t("applications.metaAds.status.connected")}
                    </Badge>
                  ) : (
                    <Badge variant="negative">
                      {t("applications.metaAds.status.expired")}
                    </Badge>
                  )}
                </Box>
              ) : null}
              <Box sx={styles.buttonGroup}>
                {hasToken ? (
                  <>
                    <Button
                      variant="secondary"
                      onClick={handleConnect}
                      loading={
                        metaAdsLogin.isPending || connectMutation.isPending
                      }
                    >
                      {t("applications.metaAds.actions.reconnect")}
                    </Button>
                    <Button
                      variant="plain"
                      onClick={() => setIsDisconnectModalOpen(true)}
                      disabled={disconnectMutation.isPending}
                    >
                      {t("applications.metaAds.actions.disconnect")}
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="primary"
                    onClick={handleConnect}
                    loading={
                      metaAdsLogin.isPending || connectMutation.isPending
                    }
                  >
                    {t("common.connect")}
                  </Button>
                )}
              </Box>
            </Stack>

            {/* Ad Accounts section */}
            <Stack gap="16px">
              <Stack gap="8px">
                <Typography
                  variant="h3"
                  fontWeight="medium"
                  color={theme.colors.staticFgTitle}
                >
                  {t("applications.metaAds.adAccounts.title")}
                </Typography>
                <Typography variant="note" color={theme.colors.staticFgNote}>
                  {t("applications.metaAds.adAccounts.description")}
                </Typography>
              </Stack>
              <TableContainer>
                <Table aria-label="meta ads accounts table">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <Typography
                          variant="body"
                          fontWeight={700}
                          color={theme.colors.staticFgTitle}
                        >
                          {t(
                            "applications.metaAds.adAccounts.table.column.accountId",
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body"
                          fontWeight={700}
                          color={theme.colors.staticFgTitle}
                        >
                          {t(
                            "applications.metaAds.adAccounts.table.column.name",
                          )}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!hasToken ? (
                      <TableRow sx={styles.emptyRow}>
                        <TableCell colSpan={2} align="center">
                          <Stack
                            gap="12px"
                            alignItems="center"
                            sx={styles.emptyContainer}
                          >
                            <Empty style={{ width: 80, height: 64 }} />
                            <Typography
                              variant="body"
                              fontWeight={400}
                              color={theme.colors.staticFgNote}
                            >
                              {t(
                                "applications.metaAds.adAccounts.empty.notConnected",
                              )}
                            </Typography>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ) : isAccountsLoading ? (
                      Array.from({ length: 3 }).map((_, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Skeleton variant="text" width={140} height={20} />
                          </TableCell>
                          <TableCell>
                            <Skeleton variant="text" width={200} height={20} />
                          </TableCell>
                        </TableRow>
                      ))
                    ) : accounts.length === 0 ? (
                      <TableRow sx={styles.emptyRow}>
                        <TableCell colSpan={2} align="center">
                          <Stack
                            gap="12px"
                            alignItems="center"
                            sx={styles.emptyContainer}
                          >
                            <Empty style={{ width: 80, height: 64 }} />
                            <Typography
                              variant="body"
                              fontWeight={400}
                              color={theme.colors.staticFgNote}
                            >
                              {t(
                                "applications.metaAds.adAccounts.empty.noAccounts",
                              )}
                            </Typography>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ) : (
                      accounts.map((account) => (
                        <TableRow key={account.id}>
                          <TableCell>
                            <Typography
                              variant="body"
                              fontWeight={500}
                              color={theme.colors.staticFgBody}
                            >
                              {account.externalAccountId}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body"
                              fontWeight={400}
                              color={theme.colors.staticFgBody}
                            >
                              {account.name}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          </Stack>
        )}

        {isDisconnectModalOpen ? (
          <DisconnectMetaAdsModal
            open
            isLoading={disconnectMutation.isPending}
            onClose={() => setIsDisconnectModalOpen(false)}
            onConfirm={handleConfirmDisconnect}
          />
        ) : null}
      </Stack>
    );
  };

interface MetaAdsIntegrationPageProps {
  showReconnectBanner?: boolean;
}

export const MetaAdsIntegrationPage: React.FC<MetaAdsIntegrationPageProps> =
  function MetaAdsIntegrationPage({ showReconnectBanner = false }) {
    return (
      <PageLayout>
        <Suspense fallback={<BarLoading />}>
          <MetaAdsIntegrationPageInternal
            showReconnectBanner={showReconnectBanner}
          />
        </Suspense>
      </PageLayout>
    );
  };
